/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ScheduleType } from '@gh/builder-shared-data';

import {
  cleanScheduleTaskRangePredecessors,
  getScheduleTaskId,
  getScheduleTaskSubtreeRange,
  propagateExcludeTasksFromGanttChart,
  recalculateScheduleTasks,
  recalculateScheduleTaskCompletionAncestors,
  scheduleTaskArray,
  scheduleTaskMerge,
  setScheduleTaskFields,
} from '@gh/builder-shared-gantt-chart';

import {
  FormStateSnapshot,
  getFormStateSnapshotValue,
  isRouteForCreationMode,
  PlainImmutablePageState,
  UserContext,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { FormMode } from '@gh/core-ui';
import { map$ } from '@gh/rx/operators';

import {
  getScheduleEditPermission,
  isScheduleEditable,
  SCHEDULE_DETAILS_INIT,
  ScheduleDetails,
  ScheduleMasterInfo,
  ScheduleTask,
} from '../../resources/schedule-details.model';

@Injectable()
export class ScheduleDetailsFormState extends PlainImmutablePageState<ScheduleDetails> {
  masterInfo$ = this.select(({ masterInfo }) => masterInfo);
  time$ = this.select(({ time }) => time);
  startDate$ = this.time$.pipe(map$(({ startDate }) => startDate));
  endDate$ = this.time$.pipe(map$(({ endDate }) => endDate));
  duration$ = this.time$.pipe(map$(({ duration }) => duration));
  gDriveUrl$ = this.select(({ gDriveUrl }) => gDriveUrl);
  tasks$ = this.select(({ tasks }) => tasks);
  showBaseDates$ = this.select(({ showBaseDates }) => showBaseDates);

  constructor(private route: ActivatedRoute,
              private userContext: UserContext) {
    super(SCHEDULE_DETAILS_INIT);
  }

  get jobId(): ID {
    return this.value.jobId;
  }

  get salesFolderId(): ID {
    return this.value.salesFolderId;
  }

  get scheduleId(): ID {
    return this.value.scheduleId;
  }

  get jobNumber(): string {
    return getFormStateSnapshotValue(this.value.masterInfo).jobNumber;
  }

  get salesFolderName(): string {
    return getFormStateSnapshotValue(this.value.masterInfo).salesFolderName;
  }

  get type(): ScheduleType {
    return this.value.type;
  }

  build(details: ScheduleDetails): void {
    this.setupMode(details);

    this.setValue(details);
    if (this.createMode) {
      this.setDirty();
    } else {
      this.resetDirty();
    }
  }

  mergeAfterRecalculate(details: ScheduleDetails): void {
    this.setField('masterInfo', details.masterInfo);
    this.setField('time', details.time);
    this.setField(
      'tasks',
      <ScheduleTask[]>scheduleTaskArray.orderedMergeWith(
        this.value.tasks,
        details.tasks,
        (oldData, newData) => <ScheduleTask>scheduleTaskMerge(
          oldData,
          newData,
          String(getScheduleTaskId(newData)))));
  }

  setMasterInfo(snapshot: FormStateSnapshot<ScheduleMasterInfo>): void {
    this.startChangeDetection();
    this.setField('masterInfo', snapshot);
    this.setDirtyIfChanged();
  }

  setTaskField(task: ScheduleTask, field: string, value: any): void {
    const taskId = getScheduleTaskId(task);

    this.startChangeDetection();
    let tasks = scheduleTaskArray.update(
      this.value.tasks,
      taskId,
      (targetTask) => setScheduleTaskFields(targetTask, { [field]: value }));

    if (field === 'isExcludeFromGanttChart') {
      tasks = propagateExcludeTasksFromGanttChart(tasks, taskId, value);
    } else if (tasks === this.value.tasks) {
      this.endChangeDetection();
      return;
    }

    if (field === 'completionPct') {
      tasks = recalculateScheduleTaskCompletionAncestors(tasks, taskId, true);
    } else {
      tasks = recalculateScheduleTasks(tasks, true);
    }

    this.setField('tasks', <ScheduleTask[]>tasks);
    this.setDirtyIfChanged();
  }

  removeTask(task: ScheduleTask): void {
    // get range of tasks including task to be deleted and all its descendants
    const range = getScheduleTaskSubtreeRange(this.value.tasks, getScheduleTaskId(task));

    if (range) {
      // remove all predecessors for range of deleted tasks
      const tasksWithCleanedPredecessors = cleanScheduleTaskRangePredecessors(this.value.tasks, range);
      // remove all tasks from the range
      this.setField(
        'tasks',
        <ScheduleTask[]>tasksWithCleanedPredecessors
          .slice(0, range.from)
          .concat(tasksWithCleanedPredecessors.slice(range.to)));
    }
    this.setField('tasks', <ScheduleTask[]>recalculateScheduleTasks(this.value.tasks, true));
    this.setDirty();
  }

  /**
   * Copies base dates into task start/end dates
   */
  saveBaseDates(): void {
    this.startChangeDetection();
    this.setField(
      'tasks',
      <ScheduleTask[]>scheduleTaskArray.updateAll(this.value.tasks, (task) => setScheduleTaskFields(task, {
        baseStartDate: task.startDate,
        baseEndDate: task.endDate,
      })));
    this.setDirtyIfChanged();
  }

  setShowBaseDates(show: boolean): void {
    this.setField('showBaseDates', show);
  }

  private setupMode(details: ScheduleDetails): void {
    if (isRouteForCreationMode(this.route)) {
      this.setMode(FormMode.CreateMode);
    } else if (this.userContext.hasPermission(getScheduleEditPermission(details))
      && isScheduleEditable(details)) {
      this.setMode(FormMode.EditMode);
    } else {
      this.setMode(FormMode.ViewMode);
    }
  }
}
